import { useCallback, useEffect, useRef, useState, type FocusEvent, type KeyboardEvent } from "react";

function isTypeaheadKey(key: string, e: KeyboardEvent): boolean {
  return key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
}

/**
 * Shared Tab/typeahead behavior for searchable comboboxes:
 * - Typing while the trigger is focused opens the menu and seeds search
 * - Subsequent keys append (avoids select-all-on-focus replacing the first letter)
 * - ArrowDown / Enter / Space open the menu
 */
export function useComboboxTypeahead(disabled = false) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef("");
  const inputRef = useRef<HTMLInputElement>(null);
  const openingRef = useRef(false);

  const setSearchBoth = useCallback((next: string) => {
    searchRef.current = next;
    setSearch(next);
  }, []);

  const openMenu = useCallback(
    (initialSearch = "") => {
      openingRef.current = true;
      setSearchBoth(initialSearch);
      setOpen(true);
    },
    [setSearchBoth],
  );

  const onTriggerKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return;

      if (open || openingRef.current) {
        if (isTypeaheadKey(e.key, e)) {
          e.preventDefault();
          setSearchBoth(searchRef.current + e.key);
          inputRef.current?.focus();
        }
        return;
      }

      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openMenu("");
        return;
      }

      if (isTypeaheadKey(e.key, e)) {
        e.preventDefault();
        openMenu(e.key);
      }
    },
    [disabled, open, openMenu, setSearchBoth],
  );

  const onOpenChange = useCallback(
    (next: boolean) => {
      if (disabled) return;
      setOpen(next);
      if (!next) {
        openingRef.current = false;
        setSearchBoth("");
      }
    },
    [disabled, setSearchBoth],
  );

  useEffect(() => {
    if (!open) {
      openingRef.current = false;
      return;
    }
    const frame = requestAnimationFrame(() => {
      const input = inputRef.current;
      if (input) {
        input.focus();
        const len = input.value.length;
        input.setSelectionRange(len, len);
      }
      openingRef.current = false;
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const onInputFocus = useCallback((e: FocusEvent<HTMLInputElement>) => {
    const el = e.currentTarget;
    const len = el.value.length;
    requestAnimationFrame(() => el.setSelectionRange(len, len));
  }, []);

  return {
    open: open && !disabled,
    setOpen,
    search,
    setSearch: setSearchBoth,
    inputRef,
    onTriggerKeyDown,
    onOpenChange,
    commandInputProps: {
      ref: inputRef,
      value: search,
      onValueChange: setSearchBoth,
      onFocus: onInputFocus,
    },
  };
}
